import React from 'react';
import {Label, Tooltip} from '@lightricks/react-design-system';
import getFollowersCountString from '@/utils/getFollowersCountString';
import {Identity} from '@/types/identity';
import {TIKTOK_IDENTITY_IDS} from '@/components/creator-profile/constants';
import CREATOR_PROFILE_TEST_IDS from '@/components/creator-profile/testIds';
import SocialNetworkIcon from '@/components/social-network-icon';
import styles from './PlatformFollowers.module.scss';

const SUPPORTED_PROVIDERS: Record<string, string[]> = {
  instagram: ['instagram_business', 'instagram'],
  tiktok: TIKTOK_IDENTITY_IDS,
};

interface PlatformData {
  platform: string;
  followerCount: number;
  engagementRate: string | null;
  profilePageUrl: string | null;
}

interface PlatformFollowersProps {
  identities: Identity[];
}

function PlatformFollowers({identities}: PlatformFollowersProps) {
  const platforms = Object.entries(SUPPORTED_PROVIDERS)
    .map(([platform, providers]): PlatformData | null => {
      const identity = identities.find((id) => providers.includes(id.provider));
      if (identity == null || identity.cachedFollowerCount == null) return null;
      return {
        platform,
        followerCount: identity.cachedFollowerCount,
        engagementRate: identity.formattedAverageEngagementRate || null,
        profilePageUrl: identity.profilePageUrl || null,
      };
    })
    .filter((item): item is PlatformData => item != null);

  if (platforms.length === 0) return null;

  return (
    <div
      className={styles.platformFollowers}
      data-testid={CREATOR_PROFILE_TEST_IDS.HEADER__PLATFORM_FOLLOWERS}
    >
      {platforms.map((p) => {
        const content = (
          <>
            <SocialNetworkIcon name={p.platform} width={20} height={20} />
            <Label size="md">{getFollowersCountString(p.followerCount)}</Label>
          </>
        );

        return (
          <Tooltip
            key={p.platform}
            arrow
            type="light"
            placement="bottom"
            disableInteractive
            title={
              p.engagementRate ? `Engagement rate: ${p.engagementRate}` : ''
            }
          >
            {p.profilePageUrl ? (
              <a
                className={styles.platformItem}
                href={p.profilePageUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {content}
              </a>
            ) : (
              <div className={styles.platformItem}>{content}</div>
            )}
          </Tooltip>
        );
      })}
    </div>
  );
}

export default PlatformFollowers;
